import { FilterPanel, SecondaryFilterProps } from "@mcleod/dispatch";
import { AutogenLayoutMiscInvoiceProcessingReady } from "./autogen/AutogenLayoutMiscInvoiceProcessingReady";
import { MiscInvoiceProcessingLayout, MiscInvoiceProcessingTableType } from "./MiscInvoiceProcessing";
import { DataSourceExecutionEvent, ListItem, MultiButton, Snackbar, TableSelectionEvent, Toast } from "@mcleod/components";
import { MiscInvoiceProcessingSecondaryFilter } from "./MiscInvoiceProcessingSecondaryFilter";
import { MiscInvoiceProcessingAction, MiscInvoiceProcessingStepper } from "./MiscInvoiceProcessingStepper";
import { ModelRow, StringUtil } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";

export class MiscInvoiceProcessingReady extends AutogenLayoutMiscInvoiceProcessingReady implements MiscInvoiceProcessingLayout {
    public type: MiscInvoiceProcessingTableType;
    private filterPanel: FilterPanel;
    private multiButtonPrintAndPost: MultiButton;
    private _refreshTabsCallback?: () => void;

    public secondaryFilters: SecondaryFilterProps[] = [
        MiscInvoiceProcessingSecondaryFilter.FILTER_RECURRING,
        MiscInvoiceProcessingSecondaryFilter.FILTER_NON_RECURRING
    ];

    override onLoad() {
        this.filterPanel = new FilterPanel(this.secondaryFilters, this.tableMiscInvoice, this.panelSecondaryFilters);
        this.filterPanel.syncFilterVisibility(null);
        this.initializePrintAndPostButton();
    }

    // START: Getters and Setters
    set refreshTabsCallback(callback: () => void) {
        this._refreshTabsCallback = callback;
    }

    get refreshTabsCallback(): (() => void) | undefined {
        return this._refreshTabsCallback;
    }
    // END: Getters and Setters

    // START: Event Handlers
    sourceMiscBillBeforeExecution(event: DataSourceExecutionEvent) {
        event.filter.ready_to_process = "Y";
        event.filter.end_date = ">=t|="; // end_date is on or after today, or end_date is null
    }

    tableMiscInvoiceOnSelect(event: TableSelectionEvent) {
        const selectedRowCount = event.table.selectedRowCount;
        this.multiButtonPrintAndPost.enabled = selectedRowCount > 0;
        this.multiButtonPrintAndPost.showSeparator = true;
    }
    // END: Event Handlers

    initializePrintAndPostButton() {
        this.multiButtonPrintAndPost = new MultiButton({
            dropdownItems: this.createMultiButtonItems(),
            color: "primary.reverse",
            backgroundColor: "primary",
            enabled: false,
            showSeparator: true
        });

        this.multiButtonPrintAndPost.primaryButton.setProps({
            themeKey: "editRowDecorator.primaryButton",
            id: "multiButtonPrintAndPost",
            caption: "Print & Post",
            enabled: false,
            onClick: () => {
                MiscInvoiceProcessingStepper.showAsSlideout({
                    action: MiscInvoiceProcessingAction.PRINT_AND_POST,
                    selectedRows: this.tableMiscInvoice.selectedRows.map(row => row.data as ModelRow),
                    doAfterSlideOut: this.getCommonDoAfterSlideOut()
                });
            }
        });

        this.tableMiscInvoice.addRightTool(this.multiButtonPrintAndPost);
    }

    createMultiButtonItems(): ListItem[] {
        return [
            new ListItem({
                text: "Print Invoices",
                onClick: () => {
                    MiscInvoiceProcessingStepper.showAsSlideout({
                        action: MiscInvoiceProcessingAction.PRINT_ONLY,
                        selectedRows: this.tableMiscInvoice.selectedRows.map(row => row.data as ModelRow),
                        doAfterSlideOut: this.getCommonDoAfterSlideOut()
                    });
                }
            }),
            new ListItem({
                text: "Post Invoices",
                onClick: async () => {
                    const response = await CommonDialogs.showYesNo("Proceed with posting?", "Post Invoices");
                    if (response) {
                        MiscInvoiceProcessingStepper.showAsSlideout({
                            action: MiscInvoiceProcessingAction.POST_ONLY,
                            selectedRows: this.tableMiscInvoice.selectedRows.map(row => row.data as ModelRow),
                            doAfterSlideOut: this.getCommonDoAfterSlideOut()
                        });
                    }
                }
            })
        ];
    }

    private getCommonDoAfterSlideOut() {
        // postingAttempted indicates that the process was not cancelled before posting. It will be true even if no invoices were posted due to exceptions.
        return (postingAttempted: boolean, postedCount: number) => {
            if (postedCount > 0) {
                this.refreshTabsCallback?.();
                Toast.showSuccessToast(`${StringUtil.pluralString(postedCount, "invoice", "invoices")} posted successfully.`);
            } else if (postingAttempted && postedCount == 0) {
                Snackbar.showWarningSnackbar("No invoices were posted.");
            }
        };
    }
}
